import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { Category } from '@/data/mockData';
import { Input } from '@/components/ui/input';
import CategoryAccordion from '@/components/CategoryAccordion';

interface SegmentSelectionPanelProps {
  categories: Category[];
  selectedSegments: string[];
  onSegmentToggle: (segmentId: string) => void;
  onCategoryToggle: (categoryId: string) => void;
}

export const SegmentSelectionPanel = ({
  categories,
  selectedSegments,
  onSegmentToggle,
  onCategoryToggle,
}: SegmentSelectionPanelProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrer les catégories et segments en fonction de searchTerm
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) {
      return categories;
    }

    const normalizedSearch = searchTerm.toLowerCase().trim();

    return categories
      .map((category) => {
        // Filtrer les segments de la catégorie
        const filteredSegments = category.segments.filter((segment) =>
          segment.name.toLowerCase().includes(normalizedSearch)
        );

        // Ne garder la catégorie que si elle contient au moins un segment qui matche
        if (filteredSegments.length > 0) {
          return {
            ...category,
            segments: filteredSegments,
          };
        }
        return null;
      })
      .filter((category): category is Category => category !== null);
  }, [categories, searchTerm]);

  // Calculer les IDs des catégories à déplier automatiquement
  const expandedCategoryIds = useMemo(() => {
    if (!searchTerm.trim()) {
      return [];
    }
    return filteredCategories.map((category) => category.id);
  }, [filteredCategories, searchTerm]);

  return (
    <div className="w-2/5 flex flex-col overflow-hidden pr-4 min-h-0 min-w-0">
      <h3 className="text-md font-semibold text-foreground mb-3 pt-6">Sélection des catégories</h3>
      <div className="relative mb-3 pr-2 pl-1">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Rechercher un segment..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 w-full"
          aria-label="Recherche de segments"
        />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-muted scrollbar-track-background">
        {filteredCategories.length === 0 && searchTerm.trim() ? (
          <p className="text-center text-muted-foreground py-8">
            Aucun segment ne correspond à votre recherche.
          </p>
        ) : (
        <CategoryAccordion
            categories={filteredCategories}
          selectedSegments={selectedSegments}
          onSegmentToggle={onSegmentToggle}
          onCategoryToggle={onCategoryToggle}
            expandedCategoryIds={expandedCategoryIds}
        />
        )}
      </div>
    </div>
  );
};

